<template>
  <component
    :is="isCard ? Menu : 'div'"
    class="bg-white flex flex-col w-full tablet-h:w-[320px] tablet-h:h-[500px]"
    :style="styles"
  >
    <div
      class="flex items-center justify-between pl-4 pr-3 pt-[calc(env(safe-area-inset-top)+68px)] tablet-h:py-1.5 tablet-h:px-2.5 mb-5 tablet-h:mb-0"
    >
      <div
        class="font-medium text-gray-800 text-[24px] tablet-h:text-[14px] tablet-h:font-normal leading-[30px] tablet-h:leading-[20px]"
      >
        Notifications
      </div>

      <div
        class="hidden tablet-h:block text-[14px] leading-[20px] font-medium cursor-pointer text-link"
        @click="handleMarkAllAsRead"
      >
        Mark as read
      </div>

      <MenuIcon
        class="tablet-h:hidden size-5 text-gray-800 cursor-pointer"
        @click="handleMenuClick"
      />
    </div>

    <Menu.Divider class="hidden tablet-h:block" />

    <div
      class="h-full space-y-0.5 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pb-0"
      ref="container"
    >
      <component
        v-for="notification in notifications"
        :key="notification.id"
        :data-notification-id="notification.id"
        :is="getComponent(notification as Notification)"
        :notification="notification as Notification"
      />
    </div>
  </component>
</template>

<script setup lang="ts">
import FileAccessNotification from '@/shared/components/notifications/FileAccessNotification.vue'
import IdentityVerificationNotification from '@/shared/components/notifications/IdentityVerificationNotification.vue'
import InfringementNotifications from '@/shared/components/notifications/InfringementNotifications.vue'
import InviteNotification from '@/shared/components/notifications/InviteNotification.vue'
import NewAccountNotification from '@/shared/components/notifications/NewAccountNotification.vue'
import PaymentNotification from '@/shared/components/notifications/PaymentNotification.vue'
import ReceivedFileNotification from '@/shared/components/notifications/ReceivedFileNotification.vue'
import SubscriptionNotification from '@/shared/components/notifications/SubscriptionNotification.vue'
import UploadFailedNotification from '@/shared/components/notifications/UploadFailedNotification.vue'
import {
  NotificationType,
  useNotificationsQuery,
  type Notification,
} from '@/shared/graphql/legacy/generated'
import { useDevice } from '@/shared/composables/useDevice'
import { useEventListener } from '@/shared/composables/useEventListener'
import {
  isMarkedRead,
  isNotificationRead,
  isVisiblyUnread,
  resetVisiblyUnread,
  syncVisiblyUnread,
  useNotificationsRead,
} from '@/shared/composables/useNotificationsRead'
import { MenuIcon } from '@dacr/icons'
import { Menu } from '@dacr/library'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
// Notifications don't use dialogs

const { result, loading, fetchMore } = useNotificationsQuery()
const { markRead, markAllRead } = useNotificationsRead()

const device = useDevice()

const isCard = computed(
  () => device.isTabletHorizontal.value || device.isDesktop.value
)

const container = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver>()

const styles = computed(() => ({
  ...((device.isMobile.value || device.isTabletVertical.value) && {
    height: `${device.viewportHeight.value}px`,
  }),
}))

const getComponent = (notification: Notification) => {
  if (notification.type === NotificationType.UserCreated) {
    return NewAccountNotification
  }

  if (
    [
      NotificationType.UserVerificationSucess,
      NotificationType.UserVerificationFailed,
    ].includes(notification.type)
  ) {
    return IdentityVerificationNotification
  }

  if (
    [
      NotificationType.AccountPaymentRequiresAction,
      NotificationType.InvoiceOverdue,
    ].includes(notification.type)
  ) {
    return PaymentNotification
  }

  if (notification.type === NotificationType.SubscriptionDowngradeFailed) {
    return SubscriptionNotification
  }

  if (
    [
      NotificationType.FileUploadFailed,
      NotificationType.FileUploadEmptyFile,
    ].includes(notification.type)
  ) {
    return UploadFailedNotification
  }

  if (
    [NotificationType.ReceivedFile, NotificationType.ReceivedLicense].includes(
      notification.type
    )
  ) {
    return ReceivedFileNotification
  }

  if (
    [
      NotificationType.FileOwnerAccessAccepted,
      NotificationType.FileOwnerAccessPending,
      NotificationType.FileAuthorAccessAccepted,
      NotificationType.FileAuthorAccessPending,
      NotificationType.FilePrivacyAccessAccepted,
      NotificationType.TeamMemberAccessPending,
      NotificationType.TeamMemberAccessAccepted,
    ].includes(notification.type)
  ) {
    return InviteNotification
  }

  if (
    [
      NotificationType.FileOwnerAccessRemoved,
      NotificationType.FileAuthorAccessRemoved,
      NotificationType.TeamMemberAccessRemoved,
    ].includes(notification.type)
  ) {
    return FileAccessNotification
  }

  if (
    [
      NotificationType.InfringementMatchCreated,
      NotificationType.ReportCreated,
    ].includes(notification.type)
  ) {
    return InfringementNotifications
  }
}

const allNotifications = computed(
  () => result.value?.notifications.notifications ?? []
)

const rawNotifications = computed(() =>
  allNotifications.value.filter((notification) =>
    getComponent(notification as Notification)
  )
)

const notifications = computed(() =>
  rawNotifications.value.map((notification) => ({
    ...notification,
    read: !isVisiblyUnread(notification.id),
  }))
)

const unrenderableUnreadIds = computed(() =>
  allNotifications.value
    .filter(
      (notification) =>
        !getComponent(notification as Notification) &&
        !isNotificationRead(notification)
    )
    .map((notification) => notification.id)
)

const observeNotificationsElements = () => {
  if (!observer.value) return

  Array.from(
    container.value?.querySelectorAll('[data-notification-id]') ?? []
  ).forEach((el) => {
    const id = el.getAttribute('data-notification-id')

    if (!id || isMarkedRead(id)) return

    observer.value?.observe(el)
  })
}

const fetchNextPage = async () => {
  const nextValue = result.value?.notifications.next

  if (nextValue && !loading.value) {
    await fetchMore({
      variables: {
        next: nextValue,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          __typename: 'Query',
          notifications: {
            __typename: 'NotificationsResponse',
            notifications: [
              ...previousData.notifications.notifications,
              ...newData.notifications.notifications,
            ],
            next: newData.notifications.next,
            unreadNotifications: previousData.notifications.unreadNotifications,
          },
        }
      },
    })
  }
}

const handleScroll = () => {
  const containerHeight = container.value?.scrollHeight || 0
  const scrollPos =
    (container.value?.clientHeight || 0) + (container.value?.scrollTop || 0)

  if (scrollPos / containerHeight >= 0.5) {
    fetchNextPage()
  }
}

useEventListener(container, 'scroll', handleScroll)

const handleMarkAllAsRead = () => markAllRead()

const handleMenuClick = () => {
  openSheet(Sheets.NotificationMenu)
}

watch(
  allNotifications,
  () => {
    markRead(unrenderableUnreadIds.value)
    syncVisiblyUnread(rawNotifications.value)
    nextTick(observeNotificationsElements)
  },
  { immediate: true }
)

onMounted(() => {
  observer.value = new IntersectionObserver(
    (entries) => {
      const ids: string[] = []
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        const notificationId = entry.target.getAttribute('data-notification-id')

        if (!notificationId) return

        ids.push(notificationId)
        observer.value?.unobserve(entry.target)
      })

      markRead(ids)
    },
    {
      root: container.value,
      threshold: 0.5,
    }
  )

  observeNotificationsElements()
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
  resetVisiblyUnread()
})
</script>
